import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ChangeDetectionStrategy
} from "@angular/core";
import { getApp } from "../app";

@Component({
    selector: "app-dates-filter",
    templateUrl: "./dates-filter.component.html",
    styleUrl: "./dates-filter.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DatesFilterComponent implements OnInit {
  @Input() docType: string = "manufacturer";

  app = getApp((app) => {
    this.app = app;
  });

  /** Labels are resolved once on open, so the template binds plain values. */
  title = "";
  fromLabel = "";
  toLabel = "";

  ngOnInit(): void {
    // The panel is created every time it opens (its host sits behind an *ngIf),
    // so the labels of the column it was opened from are up to date here.
    this.title = this.columnLabel();
    this.fromLabel = this.app.field.getLabel(
      this.app.fieldId.manufacturer.from
    );
    this.toLabel = this.app.field.getLabel(this.app.fieldId.manufacturer.to);
  }

  constructor(private elementRef: ElementRef) {}

  // Listen for all document clicks
  @HostListener("document:mousedown", ["$event"])
  onClickOutside(event: MouseEvent) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.app.datesFilter.showComponent = false;
      // this.app.datesFilter.clear();
    }
  }

  /** The date column the picker was opened from, falling back to the "Updated on" column. */
  activeField() {
    return (
      this.app.datesFilter.field ?? this.app.fieldId.manufacturer.lastUpdatedOn
    );
  }

  /** Label of the column being filtered, shown in the panel header. */
  private columnLabel() {
    const field =
      this.docType === "thread"
        ? this.app.filterList.field
        : this.activeField();

    if (!field) {
      return "";
    }

    return this.app.field.getLabel(field);
  }

  onClose() {
    this.app.datesFilter.showComponent = false;
  }

  setDateMode(mode: string) {
    if (this.app.datesFilter.model.dateMode === mode) {
      return;
    }

    this.app.datesFilter.model.dateMode = mode;
    this.onDateModeChange(mode);
  }

  onDateModeChange(mode: string) {
    const { specific, from, to } = this.app.datesFilter.getColumnRangeFields(
      this.activeField()
    );

    // Switching mode drops whatever the other mode had set on this column, so the two
    // never end up applied at the same time.
    if (mode === "interval") {
      this.app.datesFilter.model.specificDate = null;

      if (this.isFilterApplied(specific)) {
        this.app.filterHeaderTable.clearFilter(specific);
      }
    }

    if (mode === "specific") {
      this.app.datesFilter.model.fromDate = null;
      this.app.datesFilter.model.toDate = null;

      if (this.isFilterApplied(from) || this.isFilterApplied(to)) {
        this.app.filterHeaderTable.clearDateRangeFilter(this.activeField());
      }
    }
  }

  private isFilterApplied(fieldId: string) {
    return this.app.filterHeaderTable.filters.some(
      (f) => f.filterLabelField === fieldId
    );
  }

  async onApply() {
    switch (this.docType) {
      case "thread": {
        const { specific, from, to } = this.app.datesFilter.getRangeFields(
          this.app.filterList.field
        );

        this.app.state.filterByPage = this.app.state.filterByPage.filter(
          (f) =>
            f.filterLabelField !== specific &&
            f.filterLabelField !== from &&
            f.filterLabelField !== to
        );

        if (this.app.datesFilter.model.dateMode === "specific") {
          if (this.app.datesFilter.model.specificDate) {
            this.app.state.filterByPage.push({
              filterLabelField: specific,
              values: this.app.datesFilter.model.specificDate,
              filterView: this.app.state.view
            });
          }
        } else {
          if (this.app.datesFilter.model.fromDate) {
            this.app.state.filterByPage.push({
              filterLabelField: from,
              values: this.app.datesFilter.model.fromDate,
              filterView: this.app.state.view
            });
          }
          if (this.app.datesFilter.model.toDate) {
            this.app.state.filterByPage.push({
              filterLabelField: to,
              values: this.app.datesFilter.model.toDate,
              filterView: this.app.state.view
            });
          }
        }

        this.app.spinner.showSpinner();
        await this.app.thread.getThreadSearchResult("1");
        this.app.spinner.hideSpinner();
        this.app.paginator.resetPagination();
        break;
      }
      case "manufacturer":
      default: {
        const { specific, from, to } =
          this.app.datesFilter.getColumnRangeFields(this.activeField());

        if (this.app.datesFilter.model.dateMode === "specific") {
          if (this.app.datesFilter.model.specificDate) {
            this.app.filterHeaderTable.filter(
              specific,
              this.app.datesFilter.model.specificDate
            );
          }
        } else {
          this.app.filterHeaderTable.filterDateRange({
            [from]: this.app.datesFilter.model.fromDate,
            [to]: this.app.datesFilter.model.toDate
          });
        }
        break;
      }
    }
    this.app.datesFilter.showComponent = false;
  }
}
